import type { ControlBridgeQueueItem } from './deriveViewModel';

export interface UserResourceLookup {
  fullName?: string | null;
  username?: string | null;
  email?: string | null;
}

/** Strip person: prefix and lowercase for stable assignee matching. */
export function normalizeResourceRef(ref: string | undefined | null): string | undefined {
  if (!ref) return undefined;
  const text = ref.trim();
  if (!text) return undefined;
  const bare = text.startsWith('person:') ? text.slice(7).trim() : text;
  return bare || undefined;
}

function refTokens(ref: string): string[] {
  const lower = ref.toLowerCase();
  const parts = lower.split(/[-_/]/).filter(Boolean);
  return [lower, ...parts];
}

/** Match logged-in user to a kryspace resource profile ref from queue assignees. */
export function resolveUserResourceProfileRef(
  queueItems: ControlBridgeQueueItem[],
  user?: UserResourceLookup | null
): string | undefined {
  if (!user) return undefined;

  const emailLocal = user.email?.split('@')[0]?.trim().toLowerCase();
  const username = user.username?.trim().toLowerCase();
  const fullName = user.fullName?.trim();
  const nameParts = (fullName?.toLowerCase().split(/\s+/) ?? []).filter(Boolean);
  const firstName = nameParts[0];
  const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

  const candidates = new Set(
    [emailLocal, username, username?.split('.')[0], firstName, lastName].filter(Boolean) as string[]
  );

  for (const item of queueItems) {
    const rawRef = item.assignedResourceRef || item.res;
    const ref = normalizeResourceRef(rawRef);
    if (!ref) continue;

    const refLower = ref.toLowerCase();
    const tokens = refTokens(ref);
    const label = (item.assignedResourceLabel || '').toLowerCase();
    const labelFirst = label.split(/[—\-|/]/)[0]?.trim().split(/\s+/)[0];

    for (const token of Array.from(candidates)) {
      if (!token || token.length < 2) continue;
      if (
        refLower === token ||
        refLower.endsWith(`-${token}`) ||
        refLower.endsWith(`_${token}`) ||
        refLower.includes(`-${token}-`) ||
        tokens.includes(token)
      ) {
        return ref;
      }
      // co-prh-orlin / ai-op-resource-profile-orlin style
      if (refLower.includes(`prh-${token}`) || refLower.includes(`profile-${token}`)) {
        return ref;
      }
      if (label.includes(token) || labelFirst === token) {
        return ref;
      }
    }

    // Full-name fragment in label (e.g. "Orlin Wetzker")
    if (fullName && label.includes(fullName.toLowerCase())) {
      return ref;
    }
  }

  return undefined;
}
